import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, ConfirmDialog, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ScrollText, Loader2 } from "lucide-react";

export default function Actes() {
  const { canWrite } = useAuth();
  const [actes, setActes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nom: "", description: "" });
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get("/actes");
    setActes(data);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ nom: "", description: "" }); setDialog(true); };
  const openEdit = (a) => { setEditing(a); setForm({ nom: a.nom, description: a.description || "" }); setDialog(true); };

  const save = async () => {
    if (!form.nom.trim()) { toast.error("Le nom est obligatoire"); return; }
    try {
      if (editing) await api.put(`/actes/${editing.id}`, form);
      else await api.post("/actes", form);
      toast.success(editing ? "Acte modifié" : "Acte créé");
      setDialog(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/actes/${confirm.id}`);
      toast.success("Acte supprimé");
      setConfirm(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setConfirm(null);
    }
  };

  return (
    <div data-testid="actes-page">
      <PageHeader title="Actes Générateurs" subtitle="Paramétrage des actes générateurs de recettes">
        {canWrite && (
          <Dialog open={dialog} onOpenChange={setDialog}>
            <DialogTrigger asChild>
              <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800" data-testid="add-acte-btn">
                <Plus className="mr-1.5 h-4 w-4" /> Nouvel acte
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="acte-dialog">
              <DialogHeader>
                <DialogTitle>{editing ? "Modifier l'acte" : "Nouvel acte générateur"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-1.5">
                  <Label>Nom de l'acte *</Label>
                  <Input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })}
                    placeholder="Ex: Réseau International" data-testid="acte-nom" />
                </div>
                <div className="space-y-1.5">
                  <Label>Description</Label>
                  <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Description facultative" data-testid="acte-description" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialog(false)}>Annuler</Button>
                <Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="save-acte-btn">Enregistrer</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      <Card className="border-slate-200 shadow-sm">
        {loading ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : actes.length === 0 ? (
          <EmptyState icon={ScrollText} title="Aucun acte générateur" hint="Créez votre premier acte générateur pour commencer." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Nom</TableHead>
                <TableHead>Description</TableHead>
                {canWrite && <TableHead className="w-28 text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {actes.map((a) => (
                <TableRow key={a.id} className="hover:bg-slate-50" data-testid="acte-row">
                  <TableCell className="font-medium text-slate-800">{a.nom}</TableCell>
                  <TableCell className="text-slate-500">{a.description || "—"}</TableCell>
                  {canWrite && (
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(a)} data-testid="edit-acte-btn">
                        <Pencil className="h-4 w-4 text-slate-500" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setConfirm(a)} data-testid="delete-acte-btn">
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => !o && setConfirm(null)}
        title="Supprimer l'acte générateur ?"
        description={`L'acte « ${confirm?.nom} » sera définitivement supprimé.`}
        onConfirm={remove}
      />
    </div>
  );
}
